import { MapPin, X } from "@app/ui";
import { useEffect, useRef, useState } from "react";
import { motion, springs, useReducedMotion } from "../../../../lib/motion/index.js";
import { useAppLocale } from "../../../../lib/useAppLocale.js";
import { useI18n } from "../../../../lib/useI18n.js";

interface Props {
  value: string;
  onChange: (v: string) => void;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
}

/**
 * Location field below date/time. Mirrors the title's commit-morph: a full
 * autocomplete input while editing, a small bold "📍 place" line once set (tap
 * to re-edit), and an "Add location" affordance when empty. Backed by Nominatim
 * (OpenStreetMap); degrades to plain free text offline. Location is whole-series
 * free text — no coords/minimap on the phone sheet.
 */
export function LocationFieldMobile({ value, onChange }: Props) {
  const { t } = useI18n();
  const locale = useAppLocale();
  const reduce = useReducedMotion();
  const [editing, setEditing] = useState(false);
  const [results, setResults] = useState<NominatimResult[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) requestAnimationFrame(() => inputRef.current?.focus());
  }, [editing]);

  useEffect(() => {
    if (!editing || value.trim().length < 2) { setResults([]); return; }
    const ctl = new AbortController();
    const tmr = setTimeout(async () => {
      try {
        const r = await fetch(
          "https://nominatim.openstreetmap.org/search?format=json&limit=5&q=" + encodeURIComponent(value),
          { signal: ctl.signal, headers: { "Accept-Language": locale } },
        );
        const data: unknown = await r.json();
        setResults(Array.isArray(data) ? (data as NominatimResult[]) : []);
      } catch { /* offline / aborted → plain text field */ }
    }, 280);
    return () => { clearTimeout(tmr); ctl.abort(); };
  }, [locale, value, editing]);

  const commit = () => { setResults([]); setEditing(false); };

  if (!editing) {
    return (
      <motion.div layout={!reduce} transition={reduce ? { duration: 0 } : springs.snappy} className="px-1">
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="flex w-full items-center gap-1.5 truncate px-1 py-1 text-left text-sm"
        >
          <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
          {value.trim() ? <span className="truncate font-semibold">{value}</span> : <span className="text-muted-foreground">{t("taskForm.addLocation")}</span>}
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div layout={!reduce} transition={reduce ? { duration: 0 } : springs.snappy} className="relative px-1">
      <div className="flex items-center gap-1.5 rounded-lg border bg-background px-3 py-2">
        <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTimeout(commit, 120)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
          placeholder={t("taskForm.location")}
          autoComplete="off"
          className="min-w-0 flex-1 bg-transparent text-base outline-none"
        />
        {value && (
          <button type="button" aria-label={t("taskForm.clear")} onClick={() => { onChange(""); inputRef.current?.focus(); }}>
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>
      {results.length > 0 && (
        <div className="absolute inset-x-1 top-full z-10 mt-1 overflow-hidden rounded-lg border bg-card shadow-xl">
          {results.map((r) => {
            const parts = r.display_name.split(",");
            return (
              <button
                key={r.place_id}
                type="button"
                className="block w-full truncate px-3 py-2 text-left text-sm hover:bg-accent"
                onMouseDown={(e) => { e.preventDefault(); onChange(parts.slice(0, 2).join(",").trim()); commit(); }}
              >
                {parts[0]!.trim()}
                {parts[1] && <span className="text-muted-foreground">, {parts[1]!.trim()}</span>}
              </button>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
